import React, { useState } from "react";
import { ChevronRight, Users, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const INTENTS = [
  { id: "friendship", label: "Friendship", icon: Users, description: "Looking for supportive friendships" },
  { id: "dating", label: "Dating", icon: Heart, description: "Open to romantic connections" },
  { id: "long_term", label: "Long-term Partnership", icon: Sparkles, description: "Seeking a committed relationship" },
];

const TIMELINES = [
  { id: "no_rush", label: "No rush, taking it slow" },
  { id: "within_year", label: "Open to something within a year" },
  { id: "ready_now", label: "Ready for something serious now" },
];

const EXCLUSIVITY = [
  { id: "exclusive", label: "Looking for exclusivity" },
  { id: "open", label: "Open to non-exclusive dating" },
  { id: "unsure", label: "Not sure yet" },
];

const MARRIAGE_OPTIONS = [
  { id: "yes", label: "Yes, I am open to marriage" },
  { id: "maybe", label: "Maybe, not sure yet" },
  { id: "no", label: "No, not interested in marriage" },
];

const RelationshipIntentStep = ({ onNext }) => {
  const [intent, setIntent] = useState("");
  const [timeline, setTimeline] = useState("no_rush");
  const [exclusivity, setExclusivity] = useState("exclusive");
  const [openToMarriage, setOpenToMarriage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!intent) {
      toast.error("Please select what you're looking for");
      return;
    }
    
    if ((intent === "dating" || intent === "long_term") && !openToMarriage) {
      toast.error("Please answer the marriage question");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.submitRelationshipIntent({
        relationship_intent: intent,
        timeline,
        exclusivity,
        open_to_marriage: openToMarriage || null
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">What Are You Looking For?</h2>
      <p className="text-gray-600 mb-6">We'll match you with people seeking the same thing</p>

      {/* Relationship Intent */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          I'm looking for...
        </label>
        <div className="space-y-2">
          {INTENTS.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setIntent(item.id)}
                className={`w-full px-4 py-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                  intent === item.id
                    ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  intent === item.id ? 'bg-[#1C3A7A] text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className={`font-medium ${intent === item.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                    {item.label}
                  </p>
                  <p className="text-gray-500 text-sm">{item.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline - Only show for dating/long_term */}
      {(intent === "dating" || intent === "long_term") && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your timeline
          </label>
          <div className="space-y-2">
            {TIMELINES.map(item => (
              <button
                key={item.id}
                onClick={() => setTimeline(item.id)}
                className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                  timeline === item.id
                    ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Exclusivity - Only show for dating/long_term */}
      {(intent === "dating" || intent === "long_term") && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exclusivity preference
          </label>
          <div className="space-y-2">
            {EXCLUSIVITY.map(item => (
              <button
                key={item.id}
                onClick={() => setExclusivity(item.id)}
                className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                  exclusivity === item.id
                    ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Marriage Question - Only show for dating/long_term */}
      {(intent === "dating" || intent === "long_term") && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Are you open to marriage?
          </label>
          <div className="space-y-2">
            {MARRIAGE_OPTIONS.map(item => (
              <button
                key={item.id}
                onClick={() => setOpenToMarriage(item.id)}
                className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                  openToMarriage === item.id
                    ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={submitting || !intent}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default RelationshipIntentStep;
